import { ViewProps } from "@fullcalendar/core/internal";
import { createContext, Dispatch, SetStateAction, useContext, useEffect, useMemo, useRef, useState } from "react";
import { DateWithoutTime } from "../../model/DateWithoutTime";
import { Weekday } from "../../model/Weekday";
import useResizeObserver from "use-resize-observer";
import { DateTime } from "luxon";
import { autovioColors } from "../backofficeTheme";
import { Tooltip, Typography, useMediaQuery } from "@mui/material";
import type { Theme } from "@mui/material";
import { Column } from "../Column";
import { CalendarEventCardContent } from "./CalendarEventCardContent";
import { EventDialog } from "../../dialogs/EventDialog";
import { Course, CourseEvent } from "../../providers/coursesProvider";
import { CourseDetailsDialog } from "../../dialogs/CourseDetailsDialog";
import { useGetList, useGetRecordId } from "react-admin";
import { IconWithText } from "../IconWithText";
import { CalendarIcon } from "../../icons/CalendarIcon";
import InstructorIcon from "@mui/icons-material/Person";
import { InstructorDisplayName } from "../InstructorDisplayName";
import { CommaSeparated } from "../CommaSeparated";
import PlaceIcon from "@mui/icons-material/Place";
import { drivingSchoolsProvider } from "../../providers/drivingSchoolsProvider";
import PeopleIcon from "@mui/icons-material/People";
import { isCourseLesson, isTheoryLesson } from "../../model/autovioCalendarEvents";
import { useDrivingSchoolCoursesCalendarContext } from "../../details/drivingSchool/DrivingSchoolCoursesCalendar";
import { PublicHoliday } from "../../utils/calendar";
import { useCurrentDrivingSchool } from "../../hooks/useCurrentDrivingSchool";
import { LoadingIndicator } from "../LoadingIndicator";
import {
  sortTheoryLectureCategories,
  theoryLectureAbbreviation,
  TheoryLectureCategory,
} from "../../model/TheoryLecture";

export function CoursesYearView(props: ViewProps) {
  const { key, dateRange, displayedMonths } = _viewRangeToProps(props);
  return <_CoursesDateRangeView key={`year:${key}`} dateRange={dateRange} displayedMonths={displayedMonths} />;
}

export function CoursesQuarterView(props: ViewProps) {
  const { key, dateRange, displayedMonths } = _viewRangeToProps(props);
  return (
    <_CoursesDateRangeView
      key={`quarter:${key}`}
      dateRange={dateRange}
      displayedMonths={displayedMonths}
      isQuarterView
    />
  );
}

interface _DisplayedMonth {
  year: number;
  month: number;
}

function _CoursesDateRangeView(props: {
  dateRange: [number, number];
  displayedMonths: _DisplayedMonth[];
  isQuarterView?: boolean;
}) {
  const { dateRange, displayedMonths, isQuarterView = false } = props;
  const [size, setSize] = useState<_CoursesYearViewSize>({});
  const sizeContextValue = useMemo(() => ({ size, setSize }), [size]);
  const drivingSchool = useCurrentDrivingSchool();
  const { id: drivingSchoolId } = drivingSchool;
  const { setCourseTypes, setTheoryCourseTypes, selectedCourseType, selectedTheoryCourseType, selectedBranchId } =
    useDrivingSchoolCoursesCalendarContext();
  const publicHolidays: Array<PublicHoliday> = useMemo(() => {
    const knownPublicHolidays = new Set<number>();
    return (selectedBranchId ? [drivingSchool.branchesById[selectedBranchId]] : drivingSchool.branches)
      .flatMap((branch) => branch.publicHolidays ?? [])
      .reduce((acc, publicHoliday) => {
        const { date } = publicHoliday;

        if (knownPublicHolidays.has(date)) {
          return acc;
        }
        knownPublicHolidays.add(date);
        if (date < dateRange[0] || date > dateRange[1]) {
          return acc;
        }
        return [...acc, { ...publicHoliday, date: DateWithoutTime.fromNumber(date) }];
      }, [] as Array<PublicHoliday>);
  }, [drivingSchool, selectedBranchId, dateRange]);
  const { data: courses } = useGetList<Course>(
    "courses",
    {
      filter: {
        drivingSchoolId,
        ...(selectedBranchId ? { branchId: selectedBranchId } : {}),
      },
      pagination: { page: 1, perPage: 9999 },
      sort: { field: "start", order: "ASC" },
      meta: {
        selectedBranchId,
      },
    },
    {
      enabled: !!drivingSchoolId,
    },
  );
  const coursesFilteredByDateRange = useMemo(
    () => courses?.filter((course) => _courseIntersectsDateRange(course, dateRange)),
    [courses, dateRange],
  );
  useEffect(() => {
    if (!coursesFilteredByDateRange) {
      return;
    }
    const courseTypes = _getCourseTypes(coursesFilteredByDateRange);
    const theoryCourseTypes = _getTheoryCourseTypes(coursesFilteredByDateRange);
    setCourseTypes(courseTypes);
    setTheoryCourseTypes(theoryCourseTypes);
  }, [coursesFilteredByDateRange, setCourseTypes, setTheoryCourseTypes]);
  const filteredCourses = useMemo(
    () =>
      coursesFilteredByDateRange?.filter((course) =>
        _courseMatchesSelectedTypeFilters(course, selectedCourseType, selectedTheoryCourseType),
      ),
    [coursesFilteredByDateRange, selectedCourseType, selectedTheoryCourseType],
  );
  return (
    <_CoursesYearViewSizeContext.Provider value={sizeContextValue}>
      <_Grid displayedMonths={displayedMonths} isQuarterView={isQuarterView} publicHolidays={publicHolidays} />
      {!filteredCourses && <LoadingIndicator />}
      {filteredCourses && (
        <_Stripes displayedMonths={displayedMonths} isQuarterView={isQuarterView} courses={filteredCourses} />
      )}
    </_CoursesYearViewSizeContext.Provider>
  );
}

interface _CoursesYearViewSize {
  width?: number;
  height?: number;
  theadHeight?: number;
}

interface _CoursesYearViewSizeContextValue {
  size: _CoursesYearViewSize;
  setSize: Dispatch<SetStateAction<_CoursesYearViewSize>>;
}

const _CoursesYearViewSizeContext = createContext<_CoursesYearViewSizeContextValue | undefined>(undefined);
const _1_to_31 = Array.from({ length: 31 }, (_, i) => i + 1);
const _monthNames = [
  "Januar",
  "Februar",
  "März",
  "April",
  "Mai",
  "Juni",
  "Juli",
  "August",
  "September",
  "Oktober",
  "November",
  "Dezember",
];
const _weekdays: Record<Weekday, string> = {
  Monday: "Mo",
  Tuesday: "Di",
  Wednesday: "Mi",
  Thursday: "Do",
  Friday: "Fr",
  Saturday: "Sa",
  Sunday: "So",
};

function _Grid(props: {
  displayedMonths: _DisplayedMonth[];
  isQuarterView: boolean;
  publicHolidays: Array<PublicHoliday>;
}) {
  const { displayedMonths, isQuarterView, publicHolidays } = props;
  const isMobile = useMediaQuery((theme: Theme) => theme.breakpoints.down("md"));
  const isAtLeast500pxWide = useMediaQuery("(min-width: 500px)");
  const { ref: tableRef, width, height } = useResizeObserver<HTMLElement>();
  const { ref: theadRef, height: theadHeight } = useResizeObserver<HTMLElement>();
  const sizeContext = useContext(_CoursesYearViewSizeContext);
  const setSize = sizeContext?.setSize;
  useEffect(() => {
    if (!setSize) {
      return;
    }
    setSize((prev) => {
      if (prev.width === width && prev.height === height && prev.theadHeight === theadHeight) {
        return prev;
      }
      return { width, height, theadHeight };
    });
  }, [setSize, width, height, theadHeight]);
  const publicHolidaysByDate = useMemo(() => {
    const result: Record<number, string> = {};
    for (const { date, name } of publicHolidays) {
      result[date.valueOf()] = name;
    }
    return result;
  }, [publicHolidays]);
  const tbodyRows = useMemo(() => {
    const today = DateWithoutTime.today();
    return _1_to_31.map((day) => (
      <tr key={day}>
        {displayedMonths.map((displayedMonth) => {
          const { year, month } = displayedMonth;
          let date: DateWithoutTime;
          try {
            date = new DateWithoutTime(year, month, day);
          } catch {
            return <td key={`${year}-${month}`} className="invalid-date" />;
          }
          const weekday = date.weekday;
          let _kw: string | undefined;
          if (weekday === "Monday") {
            _kw = `KW ${date.weekNumber.toString().padStart(2, "0")}`;
          }
          let className = weekday.toLowerCase();
          const publicHoliday = publicHolidaysByDate[date.valueOf()];
          if (publicHoliday) {
            className += " public-holiday";
          }
          if (date.year === today.year && date.month === today.month && date.day === today.day) {
            className += " today";
          }
          return (
            <td key={`${year}-${month}`} className={className}>
              <div className="grid-cell-content">
                <div>
                  <div className="day-number">{day}</div>
                  <div className="day-name">{publicHoliday ?? _weekdays[weekday]}</div>
                </div>
                {_kw && <div className="calendar-week">{_kw}</div>}
              </div>
            </td>
          );
        })}
      </tr>
    ));
  }, [displayedMonths, publicHolidaysByDate]);
  return (
    <div className="fc-timegrid-slots">
      <table ref={tableRef}>
        <thead ref={theadRef}>
          <tr>
            {displayedMonths.map((displayedMonth) => {
              const monthName = _monthNames[displayedMonth.month - 1];
              return (
                <th key={`${displayedMonth.year}-${displayedMonth.month}`} aria-label={monthName}>
                  {isQuarterView || !isMobile
                    ? monthName
                    : isAtLeast500pxWide
                      ? monthName.substring(0, 3)
                      : monthName.charAt(0)}
                </th>
              );
            })}
          </tr>
        </thead>
        <tbody>{tbodyRows}</tbody>
      </table>
    </div>
  );
}

type _CourseEventWithDayMonthAndYear = CourseEvent & {
  day: number;
  month: number;
  year: number;
  yyyyMMdd: number;
};

type _Course = Course & {
  /** All days (year * 10000 + month * 100 + day) on which the course has at least one lesson. */
  _days: Set<number>;
  /** Sorted by start time. */
  _events: _CourseEventWithDayMonthAndYear[];
  _color: string;
  _initiallyShownDate?: DateWithoutTime;
};

interface _Tile {
  course: _Course;
  /** Sorted by start time. */
  events: _CourseEventWithDayMonthAndYear[];
  isCourseStart: 0 | 1;
  isCourseEnd: 0 | 1;
  lessonOnPrevDay?: 1;
  lessonOnNextDay?: 1;
  color: string;
}

interface _Tiles {
  [month: number]: {
    [day: number]: undefined | { [stripePos: number]: _Tile };
  };
}

function _Stripes(props: { displayedMonths: _DisplayedMonth[]; isQuarterView: boolean; courses: Array<Course> }) {
  const { displayedMonths, isQuarterView, courses } = props;
  const drivingSchoolId = useGetRecordId();
  const [selectedCourse, setSelectedCourse_] = useState<_Course | Course | null>(null);
  const selectedCourseChangedRef = useRef(false);
  const [selectedEvent, setSelectedEvent_] = useState<CourseEvent | null>(null);
  const selectedEventChangedRef = useRef(false);
  useEffect(() => {
    const openDialog = new URL(location.href).searchParams.get("openDialog");
    if (openDialog) {
      void (async () => {
        const match1 = /^CourseDetailsDialog!([0-9a-f-]+)$/.exec(openDialog);
        if (match1) {
          const id = match1[1];
          const { data: course } = await dataProvider.getOne<Course>("courses", { id, meta: { drivingSchoolId } });
          if (!selectedCourseChangedRef.current) {
            setSelectedCourse_(course);
            return;
          }
        }
        const match2 = /^(TheoryLesson|ASFCourseSession)Dialog!([0-9a-f-]+)$/.exec(openDialog);
        if (match2) {
          const id = match2[2];
          const { data: calendarEvent } = await dataProvider.getOne<CourseEvent>("calendarEvents", { id });
          if (!selectedEventChangedRef.current) {
            setSelectedEvent_(calendarEvent);
          }
        }
      })();
    }
  }, []);
  const setSelectedCourse = (course: Course | _Course | null) => {
    setSelectedCourse_(course);
    selectedCourseChangedRef.current = true;
    // Side effect: update browser URL ...
    const url = new URL(location.href);
    if (course) {
      if (url.searchParams.get("openDialog") !== `CourseDetailsDialog!${course.id}`) {
        url.searchParams.set("openDialog", `CourseDetailsDialog!${course.id}`);
        browserHistory.replace(url.toString());
      }
    } else {
      url.searchParams.delete("openDialog");
      url.searchParams.delete("showHistory");
      browserHistory.replace(url.toString());
    }
  };
  const setSelectedEvent = (event: CourseEvent | null) => {
    setSelectedEvent_(event);
    selectedEventChangedRef.current = true;
    // Side effect: update browser URL ...
    const url = new URL(location.href);
    if (event) {
      if (url.searchParams.get("openDialog") !== `${event.type}Dialog!${event.id}`) {
        url.searchParams.set("openDialog", `${event.type}Dialog!${event.id}`);
        browserHistory.replace(url.toString());
      }
    } else {
      url.searchParams.delete("openDialog");
      url.searchParams.delete("showHistory");
      browserHistory.replace(url.toString());
    }
  };
  const sizeContext = useContext(_CoursesYearViewSizeContext);
  if (!sizeContext) {
    throw new Error("_CoursesYearViewSizeContext must be available.");
  }
  const { width, height, theadHeight } = sizeContext.size;
  const tiles = useMemo(() => {
    if (!(width && height && theadHeight)) {
      return;
    }
    const displayedYearMonths = new Set(displayedMonths.map((it) => it.year * 100 + it.month));
    const courses_ = _enrichCourses(courses);
    // Layout tiles as stripes ...
    let maxStripes = 0;
    const tiles: _Tiles = Object.fromEntries(displayedMonths.map((it) => [it.month, {}]));
    for (const course of courses_) {
      const firstDay = course.startDate;
      const firstDayValue = firstDay.valueOf();
      const lastDay = course.endDate;
      const lastDayValue = lastDay.valueOf();
      // Determine stripe position so that stripes do not overlap ...
      let stripePos = 0;
      outerLoop: for (;;) {
        for (let date = firstDay; date <= lastDay; date = date.nextDay) {
          const { year, month, day } = date;
          if (!displayedYearMonths.has(year * 100 + month)) {
            continue;
          }
          if (tiles[month][day]?.[stripePos]) {
            ++stripePos;
            continue outerLoop;
          }
        }
        break outerLoop;
      }
      // Update maxStripes ...
      if (maxStripes <= stripePos) {
        maxStripes = stripePos + 1;
      }
      // Update tiles and nextStripePos ...
      for (const event of course._events) {
        const { start, year, month, day } = event;
        if (!displayedYearMonths.has(year * 100 + month)) {
          continue;
        }
        let tilesOfDay = tiles[month][day];
        if (!tilesOfDay) {
          tilesOfDay = {};
          tiles[month][day] = tilesOfDay;
        }
        const tile = tilesOfDay[stripePos];
        if (!tile) {
          tilesOfDay[stripePos] = {
            course,
            events: [event],
            isCourseStart: event.yyyyMMdd === firstDayValue ? 1 : 0,
            isCourseEnd: event.yyyyMMdd === lastDayValue ? 1 : 0,
            color: isCourseLesson(event) ? event.courseModuleColor : course._color,
          };
        } else {
          // Insert event into tile.events sorted by start time (insertion sort) ...
          let j = 0;
          while (j < tile.events.length && tile.events[j].start < start) {
            ++j;
          }
          tile.events.splice(j, 0, event);
          if (j === 0 && isCourseLesson(event)) {
            // Always use the color of the first event (APP-6066) ...
            tile.color = event.courseModuleColor;
          }
        }
      }
      // Update Tile.lessonOnPrevDay, Tile.lessonOnNextDay, add tiles for course days without a lesson ...
      for (let date = firstDay; date <= lastDay; date = date.nextDay) {
        const { year, month, day } = date;
        if (!displayedYearMonths.has(year * 100 + month)) {
          continue;
        }
        let tilesOfDay = tiles[month][day];
        if (!tilesOfDay) {
          tilesOfDay = {};
          tiles[month][day] = tilesOfDay;
        }
        const tile = tilesOfDay[stripePos];
        if (tile) {
          if (course._days.has(date.prevDay.valueOf())) {
            tile.lessonOnPrevDay = 1;
          }
          if (course._days.has(date.nextDay.valueOf())) {
            tile.lessonOnNextDay = 1;
          }
        } else {
          const dateValue = date.valueOf();
          tilesOfDay[stripePos] = {
            course,
            events: [],
            isCourseStart: dateValue === firstDayValue ? 1 : 0,
            isCourseEnd: dateValue === lastDayValue ? 1 : 0,
            color: course._color,
          };
        }
      }
    }

    function* renderTiles() {
      if (!(width && height && theadHeight)) {
        return;
      }
      const widthPerMonth = width / displayedMonths.length;
      const baseStripeWidth = widthPerMonth / (maxStripes + 3);
      const standardTileWidth = 18;
      const quarterViewMaxTileWidth = standardTileWidth * 1.5;
      const absoluteMinTileWidth = 4;
      const tileWidth = isQuarterView
        ? baseStripeWidth >= standardTileWidth
          ? Math.min(quarterViewMaxTileWidth, baseStripeWidth)
          : Math.max(absoluteMinTileWidth, baseStripeWidth)
        : Math.min(standardTileWidth, baseStripeWidth);
      const dayHeight = (height - theadHeight) / 31;
      const y1 = theadHeight - dayHeight;
      const tileWidthCSS = `${Math.max(1, Math.round(tileWidth * 100) / 100 - 3)}px`; // <-- - 3 for spacing between tiles
      const tileHeightCSS = `${Math.round(dayHeight * 100) / 100}px`;
      const borderRadius = Math.round(tileWidth * 100) / 200;
      const borderRadius1CSS: Record</* isCourseStart: */ 0 | 1, Record</* isCourseEnd: */ 0 | 1, string>> = {
        0: {
          0: "0",
          1: `0 0 ${borderRadius}px ${borderRadius}px`,
        },
        1: {
          0: `${borderRadius}px ${borderRadius}px 0 0`,
          1: `${borderRadius}px`,
        },
      };
      const borderRadius2CSS: Record</* lessonOnPrevDay: */ 0 | 1, Record</* lessOnNextDay: */ 0 | 1, string>> = {
        0: {
          0: `${borderRadius}px`,
          1: `${borderRadius}px ${borderRadius}px 0 0`,
        },
        1: {
          0: `0 0 ${borderRadius}px ${borderRadius}px`,
          1: "0",
        },
      };
      for (const [index, displayedMonth] of displayedMonths.entries()) {
        const { year, month } = displayedMonth;
        const x2 = index * widthPerMonth + 2 * tileWidth;
        for (const day of _1_to_31) {
          const tilesOfDay = tiles[month][day];
          if (tilesOfDay) {
            for (let stripePos = 0; stripePos < maxStripes; ++stripePos) {
              const tile = tilesOfDay[stripePos];
              if (tile) {
                yield (
                  <Tooltip
                    key={`${month}:${day}:${stripePos}`}
                    arrow
                    placement={month < 7 ? "right-start" : "left-start"}
                    disableFocusListener
                    disableInteractive
                    title={
                      tile.course._events?.length === 1 && !tile.course.isBKFCourse ? (
                        <CalendarEventCardContent event={tile.events[0]} mode="YearViewTooltip" />
                      ) : (
                        <_CourseTooltip course={tile.course} />
                      )
                    }
                  >
                    <div
                      className="tile"
                      data-testid={`tile-${tile.course.id}@${year}-${month}-${day}`}
                      style={{
                        left: `${x2 + stripePos * tileWidth}px`,
                        top: `${y1 + day * dayHeight}px`,
                        width: tileWidthCSS,
                        height: tileHeightCSS,
                        backgroundColor: tile.course._color,
                        borderRadius: borderRadius1CSS[tile.isCourseStart][tile.isCourseEnd],
                      }}
                    >
                      <div
                        className="tile-overlay"
                        style={{ borderRadius: borderRadius1CSS[tile.isCourseStart][tile.isCourseEnd] }}
                        onClick={() => {
                          // If the course has only one lesson, open that lesson directly ...
                          if (tile.course._events.length === 1 && !tile.course.isBKFCourse) {
                            setSelectedEvent(tile.events[0]);
                          } else {
                            setSelectedCourse({
                              ...tile.course,
                              _initiallyShownDate: new DateWithoutTime(year, month, day),
                            });
                          }
                        }}
                      >
                        {tile.events.length > 0 && (
                          <div
                            className={
                              tile.lessonOnPrevDay
                                ? tile.lessonOnNextDay
                                  ? ""
                                  : "no-lesson-on-next-day"
                                : tile.lessonOnNextDay
                                  ? "no-lesson-on-prev-day"
                                  : "no-lesson-on-prev-day no-lesson-on-next-day"
                            }
                            style={{
                              backgroundColor: tile.color,
                              borderRadius: borderRadius2CSS[tile.lessonOnPrevDay ?? 0][tile.lessonOnNextDay ?? 0],
                            }}
                          />
                        )}
                      </div>
                    </div>
                  </Tooltip>
                );
              }
            }
          }
        }
      }
    }

    return Array.from(renderTiles());
  }, [displayedMonths, isQuarterView, courses, width, height, theadHeight]);

  return (
    <>
      <div className="fc-timegrid-cols">{tiles}</div>
      {selectedCourse && (
        <CourseDetailsDialog
          course={selectedCourse}
          open
          onClose={() => setSelectedCourse(null)}
          onCloned={setSelectedCourse}
        />
      )}
      {selectedEvent && <EventDialog event={selectedEvent} isOpen onClose={() => setSelectedEvent(null)} />}
    </>
  );
}

function _enrichCourses(courses: Array<Course>): Array<_Course> {
  return courses.map((course): _Course => {
    const days = new Set<number>();
    return {
      ...course,
      _days: days,
      _events: course.events.map((it) => {
        const { day, month, year } = it.start;
        // Side effect: initialize _days ...
        const yyyyMMdd = year * 10000 + month * 100 + day;
        days.add(yyyyMMdd);
        return { ...it, day, month, year, yyyyMMdd } satisfies _CourseEventWithDayMonthAndYear;
      }),
      _color: course.isBKFCourse ? course.color : autovioColors.violet,
    };
  });
}

function _courseIntersectsDateRange(course: Course, dateRange: [number, number]): boolean {
  return course.startDate.valueOf() <= dateRange[1] && course.endDate.valueOf() >= dateRange[0];
}

function _getCourseTypes(courses: Array<Course>): Array<string> {
  const courseTypes = new Set<string>();
  for (const course of courses) {
    courseTypes.add(_getCourseType(course));
  }
  return Array.from(courseTypes.values()).sort((a, b) => a.localeCompare(b, "de", { numeric: true }));
}

function _getTheoryCourseTypes(courses: Array<Course>): Array<string> {
  const theoryCourseTypes = new Set<string>();
  for (const course of courses) {
    const theoryCourseType = _getTheoryCourseType(course);
    if (theoryCourseType) {
      theoryCourseTypes.add(theoryCourseType);
    }
  }
  return Array.from(theoryCourseTypes.values()).sort((a, b) => a.localeCompare(b, "de", { numeric: true }));
}

function _getCourseType(course: Course): string {
  if (_isTheoryCourseOrTheoryLesson(course)) {
    return "Theoriekurs";
  }
  return course.name;
}

function _getTheoryCourseType(course: Course): string | undefined {
  if (!_isTheoryCourseOrTheoryLesson(course)) {
    return undefined;
  }
  if (course.events.length === 1 && isTheoryLesson(course.events[0])) {
    const { theoryLecture } = course.events[0];
    if (theoryLecture.category === "B96") {
      return "Theoriestunde • B96";
    } else if (theoryLecture.category === "Grundstoff") {
      return `Theoriestunde • Lektion ${theoryLecture.lectureNumber}`;
    } else if (theoryLecture.category === "Zusatzstoff_B") {
      return `Theoriestunde • Lektion ${theoryLectureAbbreviation(theoryLecture)} (${theoryLecture.lectureNumber + 12})`;
    } else {
      return `Theoriestunde • Lektion ${theoryLectureAbbreviation(theoryLecture)}`;
    }
  }
  if (course.events.length > 1 && course.events.every(isTheoryLesson)) {
    const categories = new Set<TheoryLectureCategory>();
    for (const theoryLesson of course.events) {
      categories.add(theoryLesson.theoryLecture.category);
    }
    const categories_ = sortTheoryLectureCategories([...categories]).map((it) => it.replace("_", " "));
    if (categories_.length === 1) {
      return `Theoriekurs (nur ${categories_[0]})`;
    } else {
      return `Theoriekurs (${categories_.join(" + ")})`;
    }
  }
}

function _isTheoryCourseOrTheoryLesson(course: Course): boolean {
  return course.events.length > 0 && course.events.every(isTheoryLesson);
}

function _courseMatchesSelectedTypeFilters(
  course: Course,
  selectedCourseType?: string,
  selectedTheoryCourseType?: string,
): boolean {
  const courseType = _getCourseType(course);
  if (selectedCourseType && courseType !== selectedCourseType) {
    return false;
  }
  const theoryCourseTypeFilterApplies = !selectedCourseType || selectedCourseType === "Theoriekurs";
  if (!selectedTheoryCourseType) {
    return true;
  }
  if (!theoryCourseTypeFilterApplies) {
    return true;
  }
  if (_getTheoryCourseType(course) !== selectedTheoryCourseType) {
    return false;
  }
  return true;
}

function _viewRangeToProps(props: ViewProps): {
  key: string;
  dateRange: [number, number];
  displayedMonths: _DisplayedMonth[];
} {
  const start = DateWithoutTime.fromDateTime(
    DateTime.fromJSDate(props.dateProfile.currentRange.start, { zone: "Europe/Berlin" }),
  );
  const endExclusive = DateWithoutTime.fromDateTime(
    DateTime.fromJSDate(props.dateProfile.currentRange.end, { zone: "Europe/Berlin" }),
  );
  const end = endExclusive.prevDay;
  const displayedMonths: _DisplayedMonth[] = [];
  for (
    let date = start.startOfMonth;
    date <= end;
    date = DateWithoutTime.fromDateTime(date.toDateTime().plus({ month: 1 }))
  ) {
    displayedMonths.push({ year: date.year, month: date.month });
  }
  return {
    key: `${start.year}-${start.month}-${start.day}:${end.year}-${end.month}-${end.day}`,
    dateRange: [start.valueOf(), end.valueOf()],
    displayedMonths,
  };
}

function _CourseTooltip(props: { course: _Course }) {
  const { course } = props;
  const instructorIds_ = new Set<string>();
  for (const event of course._events) {
    const { instructorId } = event;
    if (instructorId) {
      instructorIds_.add(instructorId);
    }
  }
  const instructorIds = Array.from(instructorIds_);
  return (
    <Column sx={{ overflow: "hidden" }}>
      <Typography variant="body2" fontWeight="bold" noWrap>
        {course.name}
      </Typography>
      <IconWithText
        icon={<CalendarIcon fontSize="inherit" />}
        text={
          course.isBKFCourse
            ? `vom ${course.start.toFormat("d.M.yyyy")} bis ${course.end.toFormat("d.M.yyyy")}`
            : `vom ${course.start.toFormat("d.M.yyyy")} bis ${course.end.toFormat("d.M.yyyy")} (${course.numEvents} Termine)`
        }
      />
      {instructorIds.length > 0 && (
        <IconWithText
          icon={<InstructorIcon fontSize="inherit" />}
          text={
            <CommaSeparated>
              {instructorIds.map((id) => (
                <InstructorDisplayName key={id} instructorId={id} fontSize="11px" noWrap />
              ))}
            </CommaSeparated>
          }
        />
      )}
      <IconWithText
        icon={<PlaceIcon fontSize="inherit" />}
        text={(() => {
          const { branchId, location } = course;
          if (location.type === "OnlineMeeting") {
            return "Online";
          }
          if (branchId) {
            const drivingSchool = drivingSchoolsProvider.getOneFromCache(course.drivingSchoolId);
            if (drivingSchool) {
              const branch = drivingSchool.branches.find((b) => b.id === branchId);
              if (branch) {
                return branch.name;
              }
            }
          }
          return `${location.city}, ${location.street}`;
        })()}
      />
      <IconWithText icon={<PeopleIcon fontSize="inherit" />} text={`${course.numAttendees} Teilnehmer`} />
    </Column>
  );
}
